'use client';

import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';
import { cn } from '@/app/utils';
import type { CategoryTabsSectionData } from '../../types';
import { encodeHttpMediaUrl } from '../../utils/media-url';
import { MediaAsset } from '../MediaAsset';
import { PortableTextContent } from '../PortableTextContent';
import { SectionShell } from '../section-shared';

const INACTIVE_W_EXPR = 'clamp(4.25rem, 9vw, 7.5rem)';
const GAP_REM = 0.5;

function extractPlainText(value?: unknown[]): string {
  if (!value) return '';
  return value
    .flatMap((block) => {
      const b = block as Record<string, unknown>;
      const children = b?.children as Array<{ text?: string }> | undefined;
      return children?.map((c) => c.text ?? '') ?? [];
    })
    .join('');
}

export function AccordionPanel({ section }: { section: CategoryTabsSectionData }) {
  const tabs = section.tabs ?? [];

  const [activeIndex, setActiveIndex] = useState(() => (tabs.length > 0 ? tabs.length - 1 : 0));

  const derivedTabs = useMemo(
    () =>
      tabs.map((tab) => ({
        tab,
        plainDescription: extractPlainText(tab.description as unknown[]),
        posterUrl: (() => {
          const raw = tab.media?.video?.coverImage?.url ?? tab.media?.image?.url ?? null;
          return raw ? encodeHttpMediaUrl(raw) : null;
        })(),
      })),
    [tabs],
  );

  if (!tabs.length) return null;

  const inactiveCount = tabs.length - 1;
  const desktopRowStyle: CSSProperties = {
    height: `calc((100cqi - ${inactiveCount} * ${INACTIVE_W_EXPR} - ${inactiveCount} * ${GAP_REM}rem) * 3 / 4)`,
  };

  return (
    <SectionShell>
      <div className="mb-12 w-full text-center sm:mb-8">
        <h2 className="font-semibold text-[36px] text-white leading-[1.2] sm:text-[28px]">{section.title}</h2>
        <PortableTextContent className="mt-4" value={section.description} />
      </div>

      {/* Desktop: 外层是 inline-size container，行高由 cqi 算出来恒定，避免切换 active 时整行重算 */}
      <div className="w-full sm:hidden" style={{ containerType: 'inline-size' }}>
        <div className="flex w-full" style={{ ...desktopRowStyle, columnGap: `${GAP_REM}rem` }}>
          {derivedTabs.map(({ tab, plainDescription, posterUrl }, index) => {
            const active = index === activeIndex;

            return (
              <button
                key={tab._key || `${tab.tabLabel}-${index}`}
                className={cn(
                  'relative h-full min-w-0 cursor-pointer overflow-hidden rounded-2xl',
                  'transition-[flex-basis,flex-grow] duration-500 ease-[cubic-bezier(0.33,1,0.68,1)]',
                  active ? 'flex-[1_1_0%]' : 'flex-[0_0_clamp(4.25rem,9vw,7.5rem)] shrink-0',
                )}
                onClick={() => setActiveIndex(index)}
                type="button"
              >
                <div className="relative h-full w-full overflow-hidden rounded-[inherit]">
                  <div className="absolute inset-0 bg-black">
                    {active ? (
                      <MediaAsset className="h-full w-full object-cover" media={tab.media} />
                    ) : posterUrl ? (
                      <img
                        alt={tab.title || 'Category preview'}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        src={posterUrl}
                      />
                    ) : (
                      <div className="h-full w-full bg-white/6" />
                    )}
                  </div>
                  {active ? (
                    <>
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-50% via-black/30 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-2 p-6 text-left">
                        <h3 className="font-semibold text-[22px] text-white leading-[1.3]">{tab.title}</h3>
                        <p className="max-w-[360px] text-sm text-white/60 leading-[1.6]">{plainDescription}</p>
                        {tab.button?.href && tab.button?.label && (
                          <a
                            className="pointer-events-auto mt-1 inline-flex h-9 items-center rounded-full bg-[#0086FF] px-5 font-medium text-[13px] text-white transition-colors hover:bg-[#0f91ff]"
                            href={tab.button.href}
                            onClick={(e) => e.stopPropagation()}
                            rel={/^https?:\/\//.test(tab.button.href) ? 'noreferrer' : undefined}
                            target={/^https?:\/\//.test(tab.button.href) ? '_blank' : undefined}
                          >
                            {tab.button.label}
                          </a>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="absolute inset-0 bg-black/60" />
                      <div className="absolute bottom-6 left-6">
                        <span className="-rotate-90 block origin-bottom-left whitespace-nowrap font-medium text-sm text-white/80">
                          {tab.tabLabel}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile: 纵向手风琴 */}
      <div className="hidden flex-col gap-2 sm:flex">
        {derivedTabs.map(({ tab, plainDescription }, index) => {
          const active = index === activeIndex;

          return (
            <button
              key={tab._key || `m-${tab.tabLabel}-${index}`}
              className={cn(
                'relative w-full cursor-pointer overflow-hidden rounded-xl',
                !active && 'transition-colors duration-300',
              )}
              onClick={() => setActiveIndex(index)}
              type="button"
            >
              {active ? (
                <div className="relative aspect-[4/3] w-full">
                  <div className="absolute inset-0 bg-black">
                    <MediaAsset className="h-full w-full object-cover" media={tab.media} />
                  </div>
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-50% via-black/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-1.5 p-4 text-left">
                    <h3 className="font-semibold text-lg text-white leading-[1.3]">{tab.title}</h3>
                    <p className="text-white/60 text-xs leading-[1.5]">{plainDescription}</p>
                    {tab.button?.href && tab.button?.label && (
                      <a
                        className="pointer-events-auto mt-1 inline-flex h-8 items-center rounded-full bg-[#0086FF] px-4 font-medium text-white text-xs transition-colors hover:bg-[#0f91ff]"
                        href={tab.button.href}
                        onClick={(e) => e.stopPropagation()}
                        rel={/^https?:\/\//.test(tab.button.href) ? 'noreferrer' : undefined}
                        target={/^https?:\/\//.test(tab.button.href) ? '_blank' : undefined}
                      >
                        {tab.button.label}
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex w-full items-center bg-white/[0.04] px-4 py-3 text-left">
                  <span className="min-w-0 flex-1 font-medium text-sm text-white/70">{tab.tabLabel}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </SectionShell>
  );
}
